import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { useSnackbar } from "../context/SnackbarContext";
import { api, type ApiCertificateTemplate } from "../services/api";
import { CERTIFICATE_TEMPLATES_QUERY_KEY, certificateTemplatesQuery, forViewer, viewerIdOf } from "../services/queries";
import {
  duplicateCertificateTemplateSettings,
  type CertificateTemplate,
  type CertificateTemplateSettings,
} from "../domain/certificates/certificateTemplates";


/** The Organisation's own certificate templates (the ready-made ones live in code). */
export const useCertificateTemplates = () => {
  const { user, token } = useAuth();
  return useQuery({
    ...forViewer(certificateTemplatesQuery(user?.schoolId, token), viewerIdOf(user)),
    enabled: !!token && !!user?.schoolId,
  });
};

/** One saved template, for this person. Two people in one Organization do not share it. */
const certificateTemplateKey = (
  schoolId: string | undefined,
  certificateTemplateId: string | undefined,
  viewerId: string | undefined
) => {
  const key = [...CERTIFICATE_TEMPLATES_QUERY_KEY, schoolId, certificateTemplateId];
  return viewerId ? [...key, viewerId] : key;
};

export const useCertificateTemplate = (certificateTemplateId: string | undefined) => {
  const { user, token } = useAuth();
  return useQuery({
    queryKey: certificateTemplateKey(user?.schoolId, certificateTemplateId, viewerIdOf(user)),
    queryFn: async () => {
      if (!token || !certificateTemplateId) throw new Error("No token");
      const response = await api.certificates.getTemplate(certificateTemplateId, token);
      return response.certificateTemplate;
    },
    enabled: !!token && !!user?.schoolId && !!certificateTemplateId,
  });
};

/** The server's reason when it gave one (e.g. "Signature must be an https URL"). */
export const failureDetail = (error: unknown) =>
  error instanceof Error && error.message ? ` ${error.message}` : " Please try again.";

/**
 * A mutation that returns a certificate template: caches what the server sent
 * back (so its page shows the new status at once) and refreshes the list.
 * Callers show their own success message.
 */
const useCertificateTemplateWrite = <Variables>(
  write: (variables: Variables, token: string) => Promise<ApiCertificateTemplate>,
  failureMessage: string
) => {
  const { user, token } = useAuth();
  const { showMessage } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: Variables) => {
      if (!token) throw new Error("No token");
      return write(variables, token);
    },
    onSuccess: (certificateTemplate) => {
      queryClient.setQueryData(
        certificateTemplateKey(user?.schoolId, certificateTemplate.certificateTemplateId, viewerIdOf(user)),
        certificateTemplate
      );
      queryClient.invalidateQueries({ queryKey: CERTIFICATE_TEMPLATES_QUERY_KEY });
    },
    onError: (error) => {
      console.error(failureMessage, error);
      showMessage({ message: failureMessage + failureDetail(error), severity: "error" });
    },
  });
};

/** Save draft: creates the certificate template the first time, then updates it. */
export const useSaveCertificateTemplateDraft = () =>
  useCertificateTemplateWrite(
    async (
      {
        certificateTemplateId,
        settings,
      }: {
        /** Missing until the first save. */
        certificateTemplateId?: string;
        settings: CertificateTemplateSettings;
      },
      token
    ) => {
      const response = certificateTemplateId
        ? await api.certificates.updateTemplate(certificateTemplateId, settings, token)
        : await api.certificates.createTemplate(settings, token);
      return response.certificateTemplate;
    },
    "Couldn't save the draft."
  );

/** Publishes a saved draft, locking its design. */
export const usePublishCertificateTemplate = () =>
  useCertificateTemplateWrite(async (certificateTemplateId: string, token) => {
    const response = await api.certificates.publishTemplate(certificateTemplateId, token);
    return response.certificateTemplate;
  }, "Couldn't publish the certificate template.");

/** A new draft copied from any certificate template, the ready-made ones included. */
export const useDuplicateCertificateTemplate = () =>
  useCertificateTemplateWrite(async (template: CertificateTemplate, token) => {
    // The ready-made ones only exist in this app, so a copy is created from its settings.
    const response = template.isBuiltIn
      ? await api.certificates.createTemplate(
          duplicateCertificateTemplateSettings(template),
          token
        )
      : await api.certificates.duplicateTemplate(template.certificateTemplateId, token);
    return response.certificateTemplate;
  }, "Couldn't create the copy.");

export const useArchiveCertificateTemplate = () =>
  useCertificateTemplateWrite(async (certificateTemplateId: string, token) => {
    const response = await api.certificates.archiveTemplate(certificateTemplateId, token);
    return response.certificateTemplate;
  }, "Couldn't archive the certificate template.");

export const useDeleteCertificateTemplate = () => {
  const { user, token } = useAuth();
  const { showMessage } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (certificateTemplateId: string) => {
      if (!token) throw new Error("No token");
      return api.certificates.deleteTemplate(certificateTemplateId, token);
    },
    onSuccess: (_result, certificateTemplateId) => {
      // Drop the deleted one rather than refetching it (that would 404).
      queryClient.removeQueries({
        queryKey: certificateTemplateKey(user?.schoolId, certificateTemplateId, viewerIdOf(user)),
      });
      const viewerId = viewerIdOf(user);
      queryClient.invalidateQueries({
        queryKey: viewerId
          ? [...CERTIFICATE_TEMPLATES_QUERY_KEY, user?.schoolId, viewerId]
          : [...CERTIFICATE_TEMPLATES_QUERY_KEY, user?.schoolId],
        exact: true,
      });
      showMessage({ message: "Certificate template deleted", severity: "success" });
    },
    onError: (error) => {
      console.error("Failed to delete certificate template:", error);
      showMessage({
        message: "Couldn't delete the certificate template." + failureDetail(error),
        severity: "error",
      });
    },
  });
};
